<template>
  <el-container class="layout-container">
    <el-aside width="220px" class="sidebar">
      <div class="logo">HybridORS</div>
      <el-menu
        :default-active="$route.path"
        background-color="transparent"
        text-color="var(--hrs-sidebar-text)"
        active-text-color="var(--hrs-sidebar-active-text)"
        router
      >
        <el-menu-item index="/dashboard">
          <i class="el-icon-s-home"></i>
          <span slot="title">Dashboard</span>
        </el-menu-item>
        <template v-for="route in menuRoutes">
          <el-submenu v-if="route.children && route.children.length" :key="route.path" :index="route.path">
            <template slot="title">
              <i :class="route.meta && route.meta.icon ? route.meta.icon : 'el-icon-folder'"></i>
              <span>{{ route.meta && route.meta.title }}</span>
            </template>
            <el-menu-item
              v-for="child in route.children"
              :key="route.path + '/' + child.path"
              :index="route.path + '/' + child.path"
            >
              {{ child.meta && child.meta.title }}
            </el-menu-item>
          </el-submenu>
        </template>
      </el-menu>
    </el-aside>
    <el-container>
      <el-header height="64px" class="main-header">
        <span class="header-title">{{ pageTitle }}</span>
        <div class="header-right">
          <el-popover
            v-model="noticeVisible"
            placement="bottom-end"
            width="380"
            trigger="click"
            popper-class="hrs-notice-popover"
            @show="onNoticeShow"
          >
            <header-notice-panel
              ref="noticePanel"
              @count-change="noticeCount = $event"
              @navigate="noticeVisible = false"
            />
            <el-badge
              slot="reference"
              :value="noticeCount"
              :max="99"
              :hidden="noticeCount <= 0"
              class="header-badge"
            >
              <i class="el-icon-bell header-icon" title="Notifications"></i>
            </el-badge>
          </el-popover>
          <i
            class="el-icon-question header-icon"
            title="Help"
            @click="helpVisible = true"
          ></i>
          <theme-switcher />
          <header-help-dialog :visible.sync="helpVisible" />
          <el-dropdown trigger="click" @command="onUserCommand">
            <span class="user-block">
              <span class="user-avatar">{{ avatarText }}</span>
              <span>{{ userName }}</span>
              <i class="el-icon-arrow-down"></i>
            </span>
            <el-dropdown-menu slot="dropdown">
              <el-dropdown-item command="changePassword">Change Password</el-dropdown-item>
              <el-dropdown-item divided command="logout">Sign Out</el-dropdown-item>
            </el-dropdown-menu>
          </el-dropdown>
          <header-change-password-dialog
            :visible.sync="changePasswordVisible"
            @success="onPasswordChanged"
          />
        </div>
      </el-header>
      <el-main class="main-content">
        <router-view />
      </el-main>
    </el-container>
  </el-container>
</template>

<script>
import HeaderNoticePanel from '@/components/layout/HeaderNoticePanel.vue'
import HeaderHelpDialog from '@/components/layout/HeaderHelpDialog.vue'
import ThemeSwitcher from '@/components/layout/ThemeSwitcher.vue'
import HeaderChangePasswordDialog from '@/components/layout/HeaderChangePasswordDialog.vue'
import { alertApi } from '@/api/health'

export default {
  components: {
    HeaderNoticePanel,
    HeaderHelpDialog,
    ThemeSwitcher,
    HeaderChangePasswordDialog
  },
  data() {
    return {
      noticeVisible: false,
      noticeCount: 0,
      helpVisible: false,
      changePasswordVisible: false,
      noticeTimer: null
    }
  },
  computed: {
    menuRoutes() {
      return this.$store.state.routes || []
    },
    pageTitle() {
      return (this.$route.meta && this.$route.meta.title) || 'HybridORS Admin'
    },
    userName() {
      return this.$store.state.name || 'Administrator'
    },
    avatarText() {
      const name = this.userName
      return name ? name.charAt(0) : 'A'
    }
  },
  mounted() {
    this.refreshNoticeCount()
    this.noticeTimer = setInterval(this.refreshNoticeCount, 60000)
  },
  beforeDestroy() {
    if (this.noticeTimer) clearInterval(this.noticeTimer)
  },
  methods: {
    onUserCommand(cmd) {
      if (cmd === 'changePassword') {
        this.changePasswordVisible = true
        return
      }
      if (cmd === 'logout') this.logout()
    },
    onPasswordChanged() {
      this.logout()
    },
    onNoticeShow() {
      this.$nextTick(() => {
        if (this.$refs.noticePanel) this.$refs.noticePanel.loadNotices()
      })
    },
    refreshNoticeCount() {
      alertApi.list({ pageNum: 1, pageSize: 1, status: 'active' })
        .then(res => {
          this.noticeCount = Number((res.data && res.data.total) || 0)
        })
        .catch(() => {
          this.noticeCount = 0
        })
    },
    logout() {
      this.$store.commit('CLEAR_AUTH')
      this.$router.push('/login')
    }
  }
}
</script>

<style scoped>
.logo {
  height: 64px;
  line-height: 64px;
  text-align: center;
  color: #fff;
  font-weight: 700;
  font-size: 18px;
  background: rgba(0, 0, 0, 0.15);
}
.header-title {
  font-size: 16px;
  font-weight: 600;
  color: #1d2129;
}
.header-right {
  display: flex;
  align-items: center;
  gap: 16px;
}
.header-icon {
  font-size: 18px;
  color: #4e5969;
  cursor: pointer;
  transition: color 0.15s;
}
.header-icon:hover {
  color: var(--hrs-primary);
}
.header-badge >>> .el-badge__content {
  border: none;
}
.user-block {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  color: #1d2129;
}
.user-avatar {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: var(--hrs-primary);
  color: #fff;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
}
</style>

<style>
.sidebar .el-menu {
  border-right: none;
}
.sidebar .el-menu-item.is-active,
.sidebar .el-submenu .el-menu-item.is-active {
  background-color: var(--hrs-sidebar-active-bg) !important;
  color: var(--hrs-sidebar-active-text) !important;
}
.sidebar .el-menu-item:hover,
.sidebar .el-submenu__title:hover {
  background-color: rgba(255, 255, 255, 0.08) !important;
}
</style>
